import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/envago/Button/Button';
import Information, { InformationType } from '../../../components/envago/Information/Information';
import MeterReadingInput from '../../../components/envago/MeterReadingInput/MeterReadingInput';
import MeterReadingTarif from '../../../components/envago/MeterReadingTarif/MeterReadingTarif';
import MeterReadingUnit from '../../../components/envago/MeterReadingUnit/MeterReadingUnit';
import JobComment from '../../../components/envago/JobComment/JobComment';
import { AccumulatorState } from '../helper/registerAccumulator';
import { METER_TYPES, shouldShowObis } from '../../../helper/MeterHelper';
import { confirmStepCount, ConfirmTaskValidation, ConfirmValidationFeatures, ConfirmValidationSummary } from '../helper/confirmValidation';

interface ConfirmReadingsPanelProps {
    accumulator: AccumulatorState;
    // Which stepper step is active: one step per expected register, plus — when the comment
    // requirement kicks in — a dedicated reading-hint step at the end (see confirmStepCount).
    activeIndex: number;
    // Plausibility enforcement, mirroring the manual flow (see confirmValidation.ts): the same
    // flags decide between warning, required comment/image and a hard strict-validation block.
    features: ConfirmValidationFeatures;
    validation: ConfirmValidationSummary;
    comment: string | undefined;
    onChangeComment: (text: string | undefined) => void;
    onChangeValue: (taskId: string, value: string) => void;
    // Advance to the next step, or — on the last one — submit everything.
    onNext: () => void;
    onBack: () => void;
    // Mirrors config.meterReading.maskPostDecimals from the classic flow: post-decimal digits are
    // not shown/editable here, only the pre-decimal part is fed in and reported back.
    maskPostDecimals?: boolean;
    // Medium of the matched meter, resolved by the screen from the job (meterType, obis fallback).
    // Decides with shouldShowObis whether a single register shows its obis — same rule as the
    // manual flow in JobDetails.
    meterType?: METER_TYPES;
    // The results are on their way to the server — the final submit stays locked until the
    // screen knows the outcome, so they cannot be sent twice (qa#128).
    submitting?: boolean;
}

// The per-register implausibility reason, with the manual flow's precedence and wording
// (JobDetails.tsx:1230-1241). Used below the value input AND in the reading-hint step's recap.
const reasonKeyFor = (v: ConfirmTaskValidation): string | undefined => {
    if (v.continuousError) {
        return 'jobs.details.warnings.continuousReadingsValidationWarningText';
    }
    if (v.historicError) {
        return v.isFeedInMeter ? 'jobs.details.warnings.historicValidationFeedInMeterWarningText' : 'jobs.details.warnings.historicValidationWarningText';
    }
    if (v.rangeError) {
        return 'jobs.details.warnings.validationWarningText';
    }
    return undefined;
};

// Confirm step, one register at a time: the overlay above shows the display photo of the current
// register, this panel shows just that register's editable value. "Weiter" advances, the button
// on the last step submits. When an implausible value demands a reading hint (flag-driven), the
// hint gets its OWN final step — attached to a register it read as if the hint belonged to that
// tariff only. That step recaps the implausible values, since they live on earlier steps.
const ConfirmReadingsPanel = ({
    accumulator,
    activeIndex,
    features,
    validation,
    comment,
    onChangeComment,
    onChangeValue,
    onNext,
    onBack,
    maskPostDecimals = false,
    meterType,
    submitting = false,
}: ConfirmReadingsPanelProps) => {
    const { t } = useTranslation();

    const registersTotal = accumulator.expected.length;
    const displayObis = (mr: { obis?: string; rateName?: string }) =>
        shouldShowObis({ meterType, meterRateCount: registersTotal, rateName: mr.rateName }) ? mr.obis : undefined;
    const totalSteps = confirmStepCount(accumulator, validation);
    const clampedIndex = Math.min(Math.max(activeIndex, 0), Math.max(0, totalSteps - 1));
    const isCommentStep = clampedIndex >= registersTotal;
    const isLast = clampedIndex >= totalSteps - 1;
    const task = accumulator.expected[isCommentStep ? 0 : clampedIndex];
    if (!task) {
        return null;
    }

    const submitOrNextLabel = t(isLast ? 'aiReading.buttons.confirmAll' : 'aiReading.buttons.next', { count: registersTotal });

    if (isCommentStep) {
        const implausible = accumulator.expected.filter((mr) => validation.perTask[mr.taskId]?.hasAnyValidationError);
        return (
            <div className={'absolute bottom-0 left-0 right-0 bg-white rounded-t-xl p-4 max-h-[70%] overflow-y-auto'}>
                <h3>{t('jobs.details.labels.readingHint')}</h3>
                {/* Same message + Information styling the manual flow uses to demand the hint
                    (JobDetails.tsx:1569-1575); like there it disappears once a comment is set. */}
                {!comment && (
                    <div className={'mt-1 text-sm'}>
                        <Information type={InformationType.ERROR}>{t('jobs.details.warnings.hintRequiredError')}</Information>
                    </div>
                )}
                {/* Recap of the implausible values: they were confirmed on earlier steps, so the
                    user must not have to navigate back to know what needs justifying. */}
                <div className={'mt-2 flex flex-col gap-1'} data-testid="hint-implausible-values">
                    {implausible.map((mr) => {
                        const v = validation.perTask[mr.taskId];
                        const reasonKey = reasonKeyFor(v);
                        return (
                            <div key={mr.taskId} className={'rounded border border-amber-500 bg-amber-50 px-2 py-1'}>
                                <div className={'flex items-center justify-between gap-2'}>
                                    <MeterReadingTarif rateName={mr.rateName} obis={displayObis(mr)} />
                                    <div className={'flex items-center gap-1 text-gray-800'}>
                                        <span>{accumulator.collected[mr.taskId]?.value ?? ''}</span>
                                        <MeterReadingUnit className={'text-gray-600'} unit={mr.unit} obis={mr.obis} />
                                    </div>
                                </div>
                                {reasonKey && <div className={'text-xs text-amber-700'}>{t(reasonKey, v.interpolationParams)}</div>}
                            </div>
                        );
                    })}
                </div>
                <div className={'mt-3'}>
                    <JobComment
                        dropdownPosition={'relative'}
                        primary={true}
                        additionalComments={features.defaultComments}
                        onChange={onChangeComment}
                        isFreeTextOptionEnabled={features.freeTextCommentEnabled}
                    />
                    {!!comment && !validation.commentValid && (
                        <div data-testid="confirm-hint-too-short" className={'mt-1 text-sm'}>
                            <Information type={InformationType.WARNING}>{t('jobs.details.warnings.hintMinLengthWarning')}</Information>
                        </div>
                    )}
                </div>
                {validation.imageAlwaysRequiredError && (
                    <span className={'text-xs text-red-600'}>{t('jobs.details.warnings.imageAlwaysRequiredHint', { count: registersTotal })}</span>
                )}
                <div className={'mt-3 flex gap-2'}>
                    {clampedIndex > 0 && (
                        <Button secondary={true} className={'px-6'} onClick={onBack}>
                            {t('aiReading.buttons.back')}
                        </Button>
                    )}
                    <Button primary={true} block={true} disabled={validation.submitBlocked || submitting} onClick={onNext}>
                        {submitOrNextLabel}
                    </Button>
                </div>
            </div>
        );
    }

    const collected = accumulator.collected[task.taskId];
    const value = collected?.value ?? '';
    // Values taken over without consensus (e.g. via the tariff-cycle timeout) deserve extra
    // scrutiny — the user should compare them against the display photo above especially closely.
    const unconfirmed = !!collected && collected.value !== '' && !collected.accepted;

    const v = validation.perTask[task.taskId];

    // Error/warning cascade per register — same rules and i18n keys as the manual flow
    // (JobDetails.tsx:1181-1241), so both capture paths show the user identical messages.
    // The post-decimal 'invalidText' branch is omitted: the input mask enforces the structure.
    const showImageRequiredError = features.unplausibleReadingsImageRequired && !validation.hasAnyImage && !!v?.hasAnyValidationError;
    const showCommentRequiredError = features.unplausibleReadingsCommentRequired && !(comment && validation.commentValid) && !!v?.hasAnyValidationError;
    const showImageOrCommentRequiredError = showImageRequiredError && showCommentRequiredError;

    let errorText: string | undefined;
    if (showImageRequiredError && !features.unplausibleReadingsCommentRequired) {
        errorText = t(
            v.isFeedInMeter ? 'jobs.details.warnings.imageRequiredFeedInMeterError' : 'jobs.details.warnings.imageRequiredError',
            v.interpolationParams,
        );
    } else if (showImageOrCommentRequiredError) {
        errorText = t(
            v.isFeedInMeter ? 'jobs.details.warnings.imageOrCommentRequiredFeedInMeterError' : 'jobs.details.warnings.imageOrCommentRequiredError',
            v.interpolationParams,
        );
    } else if (features.strictValidation && v?.rangeError) {
        // Strict mode renders the range violation as an ERROR — submission is blocked below.
        errorText = t('jobs.details.warnings.validationWarningText', v.interpolationParams);
    }

    const warningKey = !errorText && v ? reasonKeyFor(v) : undefined;
    const warningText = warningKey ? t(warningKey, v.interpolationParams) : undefined;

    // In strict mode the user cannot advance past a register without a filled, in-range value —
    // the manual flow's equivalent is the globally disabled send button; blocking per step keeps
    // the reason visible right where the offending value is. The final submit is additionally
    // gated on the full summary (covers back-navigation and the comment/image requirements).
    const nextDisabled = isLast ? validation.submitBlocked || submitting : features.strictValidation && !!v?.strictInvalid;

    return (
        <div className={'absolute bottom-0 left-0 right-0 bg-white rounded-t-xl p-4 max-h-[70%] overflow-y-auto'}>
            <div className={'flex items-baseline justify-between'}>
                <h3>{t('aiReading.confirmPanel.title', { count: registersTotal })}</h3>
                {registersTotal > 1 && (
                    <span data-testid="confirm-step-indicator" className={'text-xs text-gray-500'}>
                        {t('aiReading.confirmPanel.stepIndicator', { index: clampedIndex + 1, total: registersTotal })}
                    </span>
                )}
            </div>
            <label className={'block mt-2'}>
                <div className={'flex items-center justify-between gap-2'}>
                    {/* Same component + convention as the reading inputs in JobDetails: rate name
                        large with the shortened obis beneath; a single-register gas/water/heat
                        meter with a rate name hides the obis (see shouldShowObis). */}
                    <MeterReadingTarif rateName={task.rateName} obis={displayObis(task)} />
                    <div className={'flex items-center gap-2'}>
                        <div
                            className={
                                'mt-1 inline-block rounded ' + (errorText ? 'ring-2 ring-red-500' : warningText || unconfirmed ? 'ring-2 ring-amber-500' : '')
                            }
                        >
                            <MeterReadingInput
                                // Remount per register: the component keeps stale internal state when the
                                // next register has no recognized value yet (same pattern as JobDetails).
                                key={task.taskId}
                                preDecimals={task.preDecimals || 8}
                                postDecimals={task.postDecimals ?? 0}
                                maskPostDecimals={maskPostDecimals}
                                value={maskPostDecimals ? value.split('.')[0] : value}
                                onChange={(next) => onChangeValue(task.taskId, next)}
                            />
                        </div>
                        <MeterReadingUnit className={'text-gray-600'} unit={task.unit} obis={task.obis} />
                    </div>
                </div>
                {/* Same presentation as the manual flow (JobDetails.tsx:1480-1491): the
                    Information boxes with icon, and warnings escalate to the ERROR look when the
                    job demands strictValidation. */}
                {errorText && (
                    <div data-testid="confirm-error-text" className={'mt-2 text-sm'}>
                        <Information type={InformationType.ERROR}>{errorText}</Information>
                    </div>
                )}
                {!errorText && warningText && (
                    <div data-testid="confirm-warning-text" className={'mt-2 text-sm'}>
                        <Information type={features.strictValidation ? InformationType.ERROR : InformationType.WARNING}>{warningText}</Information>
                    </div>
                )}
                {!errorText && !warningText && unconfirmed && (
                    <div data-testid="confirm-unconfirmed-hint" className={'mt-2 text-sm'}>
                        <Information type={InformationType.WARNING}>{t('aiReading.confirmPanel.unconfirmed')}</Information>
                    </div>
                )}
            </label>
            {isLast && validation.imageAlwaysRequiredError && (
                <span className={'text-xs text-red-600'}>{t('jobs.details.warnings.imageAlwaysRequiredHint', { count: registersTotal })}</span>
            )}
            <div className={'mt-3 flex gap-2'}>
                {clampedIndex > 0 && (
                    // px-6: the base button style only applies horizontal padding from the sm
                    // breakpoint up, so on phones the label would sit flush against the border.
                    <Button secondary={true} className={'px-6'} onClick={onBack}>
                        {t('aiReading.buttons.back')}
                    </Button>
                )}
                <Button primary={true} block={true} disabled={nextDisabled} onClick={onNext}>
                    {submitOrNextLabel}
                </Button>
            </div>
        </div>
    );
};

export default ConfirmReadingsPanel;
